import React, { PropsWithChildren, useMemo, useState } from 'react'
import { ReadyState } from 'react-use-websocket'

import { WebSocketContext } from './WebSocketContext'
import { createWebSocketMessageRouter } from './core/messageRouter'
import { createHandledMessageStore } from './core/messageStore'
import { CONNECTION_STATUS_LABELS } from './core/reconnect'
import { useWebSocketTransport } from './hooks/useWebSocketTransport'
import { TICKS_MARKET_DATA_EVENT_TYPE } from './marketData/useTicksMarketData'
import { useTicksMarketDataSubscriptions } from './marketData/useTicksMarketDataSubscriptions'

export const WebSocketProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [router] = useState(() =>
    createWebSocketMessageRouter({
      messageStoresByType: {
        [TICKS_MARKET_DATA_EVENT_TYPE]: createHandledMessageStore({ persist: false }),
      },
    })
  )
  const transport = useWebSocketTransport(router)
  const subscribeTicksMarketData = useTicksMarketDataSubscriptions(transport)
  const { coordinator, enabled, readyState, sendJsonMessage, sendMessage } = transport
  const { isLeaderTab } = coordinator

  const contextValue = useMemo(
    () => ({
      connectionStatus: CONNECTION_STATUS_LABELS[readyState],
      isConnected: readyState === ReadyState.OPEN,
      isConnecting: readyState === ReadyState.CONNECTING,
      isLeaderTab: enabled && isLeaderTab,
      readyState,
      sendJsonMessage,
      sendMessage,
      subscribeMessage: router.subscribe,
      subscribeTicksMarketData,
    }),
    [
      enabled,
      isLeaderTab,
      readyState,
      router,
      sendJsonMessage,
      sendMessage,
      subscribeTicksMarketData,
    ]
  )

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
}
